/**
 * Pure column-signature helper — derives a stable, order-independent,
 * whitespace/case-insensitive key from a file's set of header names. Used as
 * the lookup key for remembered import configurations (see
 * `import-mapping-transforms/spec.md` "Column signature computation").
 *
 * Normalization is limited to trim + lowercase + whitespace-collapse only —
 * it MUST NOT strip diacritics or substitute punctuation, so genuinely
 * different headers (e.g. `Adres` vs `Adrés`) never collide into the same
 * signature. Do NOT reuse `normalizeForMatch` (import-job-field-options.ts),
 * which additionally strips diacritics and is too aggressive for this use.
 *
 * No DB, R2, or Redis — pure logic only.
 */

export function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Computes a stable signature for a set of column headers. Headers are
 * normalized then sorted before joining, so the same header set in a
 * different order (or with different whitespace/case) yields the same
 * signature, while a differing header set yields a different one.
 */
export function computeColumnSignature(headers: string[]): string {
  const normalized = headers.map(normalizeHeader).sort();
  return normalized.join('\u0001');
}
